import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState } from '../../store';
import { authFail, setDeeplinkDto } from '../../store/actions/authAction';
import { googleAuthService } from '../../shared/services/googleAuth.service';
import { ALLOWED_AUTH_DOMAINS, GOOGLE_OAUTH_CONSTANTS, PATH_URLS } from '../../shared/constants/commonConstants';
import { centeredNotification } from '../../utils/utility';

export const DeeplinkHandler = (): null => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(({ auth }: RootState): boolean => !!auth.token);
  const authAttempted = useRef(false);
  const { pathname, search, hash } = location;
  // immediately called function to update the deeplink URL based on userActionId
  const updatedDeeplinkURL = ((): string => {
    if (pathname === PATH_URLS.INPATIENT_ROOT && search) {
      const urlParams = new URLSearchParams(search);
      const userActionId = urlParams.get('userActionId');

      if (userActionId) {
        // Determine the redirect path based on userActionId
        const redirectPath = userActionId === '1'
          ? PATH_URLS.INPATIENT_ROOT + PATH_URLS.NEW_ADMISSION
          : pathname;

        // Return the redirect path with all query parameters
        return redirectPath;
      }
    }
    // For all other cases, return the current pathname
    return pathname;
  })();

  const isFromAllowedDomain = (): boolean => {
    // Check document.referrer for the origin domain
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        return ALLOWED_AUTH_DOMAINS.some((domain: string): boolean =>
          referrerUrl.hostname === domain || referrerUrl.hostname.endsWith(`.${domain}`),
        );
      } catch (error) {
        return false;
      }
    }
    return false;
  };
  const triggerInstantAuth = (): void => {
    if (authAttempted.current) {
      return;
    }
    authAttempted.current = true;
    try {
      // original path for OAuth redirection to preserve all deeplink information
      const originalPath = sessionStorage.getItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.ORIGINAL_DEEPLINK) || updatedDeeplinkURL;
      // For deeplinks, initiate OAuth2 flow which will redirect to Google
      googleAuthService.attemptSilentLogin(originalPath);
    } catch (error) {
      setTimeout((): void => {
        centeredNotification(GOOGLE_OAUTH_CONSTANTS.ERRORS.AUTHENTICATION, 'error');
      }, 100);
    }
  };
  useEffect((): void => {
    const isDeeplink = search;

    // Skip processing if user just logged out or is on logout page
    if (pathname === '/logout' || pathname === '/' && !search) {
      return; // Don't process deeplinks during logout or on clean root page
    }

    // Only process deeplinks if they have an 'origin' query parameter
    if (isDeeplink) {
      const urlParams = new URLSearchParams(search);
      const hasOriginParam = urlParams.has('origin');
      if (!hasOriginParam) {
        return; // Skip processing if no origin parameter
      }

      const queryParams: Record<string, string> = {};
      // Convert URLSearchParams to plain object
      urlParams.forEach((value: string, key: string): void => {
        queryParams[key] = value;
      });

      // Store deeplink info in sessionStorage to persist across auth flow
      const deeplinkData = {
        isDeeplink: true,
        deeplinkPath: pathname,
        deeplinkQueryParams: queryParams,
      };

      // Also store the original deeplink path for OAuth redirection
      sessionStorage.setItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.ORIGINAL_DEEPLINK, pathname + search + hash);
      dispatch(setDeeplinkDto(deeplinkData));

      // redirect to path based on userActionId
      if (isAuthenticated && updatedDeeplinkURL !== pathname) {
        history.replace(updatedDeeplinkURL);
        return;
      } else if (!isAuthenticated && isFromAllowedDomain()) {
        // Only trigger authentication if user is not authenticated AND origin is from allowed domain
        triggerInstantAuth();
      } else if (!isAuthenticated && !isFromAllowedDomain()) {
        dispatch(authFail(GOOGLE_OAUTH_CONSTANTS.ERRORS.ALLOWED_DOMAIN));
      }
    }
  }, [pathname, search, isAuthenticated, dispatch]);

  return null;
};
